import React, { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FaEye, FaTrashAlt } from "react-icons/fa";

import SectionCard from "./SectionCard";

import {
  formatNumber,
  getTotalVisits,
  getVisitDateLabel,
} from "../utils/dashboardUtils";

export default function VisitsChartCard({
  loading,
  visits = [],
  deleting = false,
  onDeleteVisits,
}) {
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");

  const chartData = useMemo(() => {
    return visits.map((item) => ({
      date: getVisitDateLabel(item),
      count: Number(item?.count || 0),
    }));
  }, [visits]);

  const totalVisits = useMemo(() => getTotalVisits(visits), [visits]);

  const handleSubmit = (event) => {
    event.preventDefault();

    onDeleteVisits({
      dateDebut,
      dateFin,
    });
  };

  return (
    <SectionCard
      icon={FaEye}
      title="Évolution des visites"
      subtitle="Suivez la fréquentation du site public du club."
      action={
        <span className="rounded-2xl bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
          {formatNumber(totalVisits)} visites
        </span>
      }
    >
      {loading ? (
        <div className="h-[340px] animate-pulse rounded-3xl bg-slate-100 dark:bg-slate-800" />
      ) : chartData.length > 0 ? (
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="visitsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563EB" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                stroke="#94A3B8"
                tickMargin={10}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12 }}
                stroke="#94A3B8"
                width={42}
              />
              <Tooltip
                formatter={(value) => [`${value} visite(s)`, "Visites"]}
                labelFormatter={(label) => `Date : ${label}`}
                contentStyle={{
                  borderRadius: "16px",
                  border: "1px solid #E2E8F0",
                  boxShadow: "0 20px 45px rgba(15, 23, 42, 0.16)",
                }}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#2563EB"
                strokeWidth={3}
                fill="url(#visitsGradient)"
                activeDot={{ r: 6 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950">
          <p className="font-bold text-slate-800 dark:text-slate-100">
            Aucune visite enregistrée.
          </p>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Les statistiques apparaîtront ici dès que le site sera consulté.
          </p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950"
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-bold text-slate-900 dark:text-white">
              Nettoyage des visites
            </h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Supprimez les anciennes visites pour alléger la base de données.
            </p>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
          <input
            type="date"
            value={dateDebut}
            onChange={(event) => setDateDebut(event.target.value)}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-orange-500/10"
          />

          <input
            type="date"
            value={dateFin}
            onChange={(event) => setDateFin(event.target.value)}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-orange-500/10"
          />

          <button
            type="submit"
            disabled={deleting}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/20 transition hover:-translate-y-0.5 hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <FaTrashAlt />
            {deleting ? "Suppression..." : "Supprimer"}
          </button>
        </div>
      </form>
    </SectionCard>
  );
}